import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Download, FileText, FileSpreadsheet, FileType, FileBox } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Document, Packer, Paragraph, HeadingLevel, Table as DocxTable, TableRow as DxRow, TableCell as DxCell, TextRun } from "docx";
import { saveAs } from "file-saver";

export interface ExportSection {
  title: string;
  headers: string[];
  rows: (string | number)[][];
  notes?: string;
}

interface Props {
  filenameBase: string;
  sections: ExportSection[];
  label?: string;
  size?: "sm" | "default";
}

const sanitize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function exportCSV(filenameBase: string, sections: ExportSection[]) {
  const lines: string[] = [];
  sections.forEach((s) => {
    lines.push(`"${s.title}"`);
    lines.push(s.headers.map((h) => `"${h}"`).join(","));
    s.rows.forEach((r) =>
      lines.push(r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")),
    );
    lines.push("");
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, `${sanitize(filenameBase)}.csv`);
}

function exportXLSX(filenameBase: string, sections: ExportSection[]) {
  const wb = XLSX.utils.book_new();
  sections.forEach((s) => {
    const aoa = [s.headers, ...s.rows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const sheetName = s.title.slice(0, 31).replace(/[\\\/\?\*\[\]]/g, "");
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });
  XLSX.writeFile(wb, `${sanitize(filenameBase)}.xlsx`);
}

function exportPDF(filenameBase: string, sections: ExportSection[]) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  doc.setFontSize(16);
  doc.text(filenameBase, 40, 40);
  let cursorY = 70;
  sections.forEach((s, idx) => {
    if (idx > 0) cursorY += 10;
    doc.setFontSize(12);
    doc.setTextColor(0, 107, 107);
    doc.text(s.title, 40, cursorY);
    cursorY += 6;
    autoTable(doc, {
      head: [s.headers],
      body: s.rows.map((r) => r.map((v) => String(v ?? ""))),
      startY: cursorY,
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [0, 107, 107] },
      margin: { left: 40, right: 40 },
    });
    cursorY = (doc as any).lastAutoTable.finalY + 20;
    if (cursorY > 720) {
      doc.addPage();
      cursorY = 40;
    }
  });
  doc.save(`${sanitize(filenameBase)}.pdf`);
}

async function exportDOCX(filenameBase: string, sections: ExportSection[]) {
  const children: any[] = [
    new Paragraph({ text: filenameBase, heading: HeadingLevel.TITLE }),
  ];
  sections.forEach((s) => {
    children.push(new Paragraph({ text: s.title, heading: HeadingLevel.HEADING_2 }));
    if (s.rows.length === 0) {
      children.push(new Paragraph({ children: [new TextRun({ text: "(no data)", italics: true })] }));
      return;
    }
    const headerRow = new DxRow({
      children: s.headers.map(
        (h) => new DxCell({ children: [new Paragraph({ children: [new TextRun({ text: h, bold: true })] })] }),
      ),
    });
    const bodyRows = s.rows.map(
      (r) =>
        new DxRow({
          children: r.map(
            (v) => new DxCell({ children: [new Paragraph(String(v ?? ""))] }),
          ),
        }),
    );
    children.push(new DocxTable({ rows: [headerRow, ...bodyRows] }));
    children.push(new Paragraph(""));
  });
  const doc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${sanitize(filenameBase)}.docx`);
}

export const ExportMenu: React.FC<Props> = ({ filenameBase, sections, label = "Export", size = "sm" }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button size={size} variant="outline">
        <Download className="w-4 h-4 mr-1" /> {label}
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuLabel>Download as</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => exportPDF(filenameBase, sections)}>
        <FileType className="w-4 h-4 mr-2" /> PDF
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => exportXLSX(filenameBase, sections)}>
        <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => exportCSV(filenameBase, sections)}>
        <FileText className="w-4 h-4 mr-2" /> CSV
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => exportDOCX(filenameBase, sections)}>
        <FileBox className="w-4 h-4 mr-2" /> Word (.docx)
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);
